/**
 * Bliss & Birch - Collection Infinite Scroll
 * Replaces traditional numbered pagination with automatic continuous scroll
 */

if (!customElements.get('collection-infinite-scroll')) {
  class CollectionInfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.isLoading = false;
      this.observer = null;
      this.onCollectionReloaded = this.init.bind(this);
    }

    connectedCallback() {
      this.init();
      document.addEventListener('collection:reloaded', this.onCollectionReloaded);
    }

    disconnectedCallback() {
      this.cleanup();
      document.removeEventListener('collection:reloaded', this.onCollectionReloaded);
    }

    cleanup() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    init() {
      this.cleanup();
      this.isLoading = false;

      this.sentinel = this.querySelector('.infinite-scroll__sentinel');
      this.loader = this.querySelector('.infinite-scroll__loading');
      this.finished = this.querySelector('.infinite-scroll__finished');
      this.retryEl = this.querySelector('.infinite-scroll__retry');

      this.pagination = this.nextElementSibling?.matches('.bb-crawl-pagination') ? this.nextElementSibling : null;
      this.setPaginationFallback(true);
      const nextUrl = this.dataset.nextUrl;

      if (!nextUrl) {
        if (this.loader) this.loader.style.display = 'none';
        if (this.finished) this.finished.style.display = 'flex';
        this.setPaginationFallback(false);
        return;
      }

      if (this.finished) this.finished.style.display = 'none';
      if (this.retryEl) this.retryEl.style.display = 'none';

      this.setupObserver();
    }

    setupObserver() {
      if (!this.sentinel || !('IntersectionObserver' in window)) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.isLoading && this.dataset.nextUrl) {
                this.loadNextPage();
              }
            });
          },
          {
            root: null,
            rootMargin: '400px 0px 400px 0px',
            threshold: 0.01,
          }
        );

        this.observer.observe(this.sentinel);
        this.setPaginationFallback(false);
      }

      // Check if sentinel is already visible in viewport
      setTimeout(() => this.checkImmediateIntersection(), 250);
    }

    setPaginationFallback(visible) {
      if (!this.pagination) return;
      this.pagination.style.display = visible ? 'flex' : 'none';
      if (visible && this.dataset.nextUrl) {
        const nextLink = this.pagination.querySelector('.next a');
        if (nextLink) nextLink.setAttribute('href', this.dataset.nextUrl);
      }
    }

    checkImmediateIntersection() {
      if (!this.sentinel || this.isLoading || !this.dataset.nextUrl) return;

      const rect = this.sentinel.getBoundingClientRect();
      const inView = rect.top <= (window.innerHeight || document.documentElement.clientHeight) + 300;

      if (inView) {
        this.loadNextPage();
      }
    }

    renderSkeletons() {
      const currentGrid = document.getElementById('product-grid');
      if (!currentGrid) return;

      const isMobile = window.innerWidth < 750;
      const count = isMobile ? 2 : 4;

      const fragment = document.createDocumentFragment();
      for (let i = 0; i < count; i++) {
        const li = document.createElement('li');
        li.className = 'grid__item scroll-trigger--cancel bb-skeleton-item';
        li.setAttribute('aria-hidden', 'true');
        li.innerHTML = `
          <div class="bb-skeleton-card">
            <div class="bb-skeleton-media"></div>
            <div class="bb-skeleton-info">
              <div class="bb-skeleton-line bb-skeleton-line--title"></div>
              <div class="bb-skeleton-line bb-skeleton-line--subtitle"></div>
              <div class="bb-skeleton-line bb-skeleton-line--price"></div>
            </div>
          </div>
        `;
        fragment.appendChild(li);
      }
      currentGrid.appendChild(fragment);
    }

    removeSkeletons() {
      const currentGrid = document.getElementById('product-grid');
      if (!currentGrid) return;
      currentGrid.querySelectorAll('.bb-skeleton-item').forEach((el) => el.remove());
    }

    async loadNextPage() {
      const nextUrl = this.dataset.nextUrl;
      if (!nextUrl || this.isLoading) return;

      this.isLoading = true;
      this.renderSkeletons();
      if (this.loader) this.loader.style.display = 'inline-flex';
      if (this.retryEl) this.retryEl.style.display = 'none';

      try {
        const response = await fetch(nextUrl);
        if (!response.ok) {
          throw new Error(`Failed to load page: ${response.status}`);
        }

        const responseText = await response.text();
        const doc = new DOMParser().parseFromString(responseText, 'text/html');

        const newGrid = doc.getElementById('product-grid');
        const currentGrid = document.getElementById('product-grid');

        this.removeSkeletons();
        if (!newGrid || !currentGrid || !newGrid.querySelector('.grid__item')) {
          throw new Error('Product grid missing from loaded page');
        }

        if (newGrid && currentGrid) {
          const newItems = Array.from(newGrid.querySelectorAll('.grid__item'));

          newItems.forEach((item) => {
            // Guarantee image lazy loading and async decoding
            item.querySelectorAll('img').forEach((img) => {
              img.setAttribute('loading', 'lazy');
              img.setAttribute('decoding', 'async');
            });

            // Prevent scroll-trigger animation from leaving appended cards invisible
            item.classList.add('scroll-trigger--cancel', 'bb-product-fade-in');
            item.querySelectorAll('.scroll-trigger').forEach((triggerEl) => {
              triggerEl.classList.add('scroll-trigger--cancel');
            });

            currentGrid.appendChild(item);
          });

          // Dispatch event for any product badges, reviews, or wishlist scripts
          document.dispatchEvent(
            new CustomEvent('collection:products-loaded', {
              detail: {
                items: newItems,
                count: newItems.length,
                url: nextUrl,
              },
            })
          );
        }

        // Find updated next page URL from fetched DOM
        const fetchedInfiniteScroll = doc.querySelector('collection-infinite-scroll');
        const updatedNextUrl = fetchedInfiniteScroll ? fetchedInfiniteScroll.getAttribute('data-next-url') : null;

        if (updatedNextUrl) {
          this.dataset.nextUrl = updatedNextUrl;
          this.setAttribute('data-next-url', updatedNextUrl);
        } else {
          // Reached end of collection
          this.dataset.nextUrl = '';
          this.removeAttribute('data-next-url');

          this.cleanup();

          if (this.finished) {
            this.finished.style.display = 'flex';
          }
        }

        this.isLoading = false;
        this.setPaginationFallback(false);
        if (this.dataset.nextUrl && !this.observer) this.setupObserver();
        if (this.loader) this.loader.style.display = 'none';

        // If more pages remain and sentinel is still within view (e.g. large displays), load next page
        if (this.dataset.nextUrl) {
          setTimeout(() => this.checkImmediateIntersection(), 200);
        }
      } catch (error) {
        console.error('Collection infinite scroll error:', error);
        this.removeSkeletons();
        this.isLoading = false;
        if (this.loader) this.loader.style.display = 'none';
        this.cleanup();
        this.setPaginationFallback(true);
        this.showRetry(nextUrl);
      }
    }

    showRetry(retryUrl) {
      if (!this.retryEl) {
        this.retryEl = document.createElement('div');
        this.retryEl.className = 'infinite-scroll__retry';
        this.retryEl.innerHTML = `
          <p class="infinite-scroll__retry-text">Unable to load more products right now.</p>
          <button type="button" class="button button--secondary infinite-scroll__retry-btn">Retry</button>
        `;
        const retryBtn = this.retryEl.querySelector('.infinite-scroll__retry-btn');
        if (retryBtn) {
          retryBtn.addEventListener('click', () => {
            this.dataset.nextUrl = retryUrl;
            this.loadNextPage();
          });
        }
        const statusContainer = this.querySelector('.infinite-scroll__status') || this;
        statusContainer.appendChild(this.retryEl);
      }
      this.retryEl.style.display = 'flex';
    }
  }

  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
